/**
 * Shows assessment run results in the drawer: speedometer per domain, score, interpretation, and article (detailed report).
 * Data from GET /api/secure/practitioner/assessments/run/result (preferred when completedRunId is known)
 * with fallback to GET /api/secure/practitioner/assessments/runs. Articles are opened in a nested drawer.
 * Includes report narrative EMR sections (read/edit) and free-form notes; content is saved via assessment-report/save.
 */
import CloseIcon from '@mui/icons-material/Close'
import DownloadIcon from '@mui/icons-material/Download'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import ListIcon from '@mui/icons-material/List'
import SaveIcon from '@mui/icons-material/Save'
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Typography,
  alpha,
} from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import ReactSpeedometer from 'react-d3-speedometer'
import parse from 'html-react-parser'
import { toast } from 'react-toastify'
import {
  getAssessmentRuns,
  getAssessmentRunResult,
  getAssessmentReportPdf,
  getAssessmentReportByEventIdMentor,
  markBookedEventAsStarted,
  markBookedEventAsCompleted,
  saveAssessmentReportFreeForm,
} from 'src/mentor/services/practitionerAssessmentService'
import { ASSESSMENT_REPORT_NARRATIVE_SECTIONS } from 'src/mentor/components/AssessmentReport/assessmentReportSectionConfig'
import PatientHistoryTipTapEditor from 'src/mentor/components/PrescriptionMakingPage/screeningReportViewer/components/PatientHistoryTipTapEditor'

// Same gradient colors as consumer AssessmentPlayer for speedometer segments and domain strips
const ASSESSMENT_RESULT_COLORS = [
  { startColor: '#ddd6fe', endColor: '#7c3aed' },
  { startColor: '#e9bbed', endColor: '#b71ac4' },
  { startColor: '#a3f0b7', endColor: '#04d139' },
  { startColor: '#ddd6fe', endColor: '#7c3aed' },
  { startColor: '#a3f0b7', endColor: '#04d139' },
  { startColor: '#ddd6fe', endColor: '#7c3aed' },
  { startColor: '#e9bbed', endColor: '#b71ac4' },
  { startColor: '#a3f0b7', endColor: '#04d139' },
  { startColor: '#ddd6fe', endColor: '#7c3aed' },
  { startColor: '#a3f0b7', endColor: '#04d139' },
]

const ASSESSMENT_REPORT_EXTRA_NARRATIVE_SECTIONS = ASSESSMENT_REPORT_NARRATIVE_SECTIONS.filter(
  (section) => section?.field && section.id !== 'free_form_rich_text',
)

function normalizeRichTextValue(value) {
  if (typeof value === 'string') return value
  if (value == null) return ''
  return String(value)
}

function buildNarrativeFieldState(reportObj) {
  const next = {}
  for (const section of ASSESSMENT_REPORT_EXTRA_NARRATIVE_SECTIONS) {
    next[section.field] = normalizeRichTextValue(reportObj?.[section.field])
  }
  return next
}

function hasRichTextContent(value) {
  return normalizeRichTextValue(value).trim().length > 0
}

function getMinMax(rangeItem) {
  const min = rangeItem?.min ?? rangeItem?.from ?? 0
  const max = rangeItem?.max ?? rangeItem?.to ?? 0
  return { min, max }
}

function resolveAssessmentEventBookedEventId({
  run,
  report,
  triggeringBookedEventId,
  isAssessmentEventPage,
}) {
  if (isAssessmentEventPage) return triggeringBookedEventId

  const candidates = [
    run?.assessmentEventBookedEventId,
    run?.assessmentBookedEventId,
    run?.assessmentBookedEvent?.id,
    run?.run?.bookedEventId,
    run?.bookedEventId,
    report?.bookedEventId,
  ].filter(Boolean)

  for (const candidate of candidates) {
    if (candidate !== triggeringBookedEventId) return candidate
  }
  return null
}

function getRunAssessmentId(run) {
  return run?.assessmentId ?? run?.psychAssessmentId ?? run?.clinicalAssessmentId ?? null
}

function normalizeRun(run, fallbackAssessmentId = null, fallbackAssessmentTitle = null) {
  if (!run || typeof run !== 'object') return null
  return {
    ...run,
    assessmentId: getRunAssessmentId(run) ?? fallbackAssessmentId ?? null,
    assessmentTitle:
      run?.assessmentTitle ??
      run?.psychAssessmentTitle ??
      run?.clinicalAssessmentTitle ??
      fallbackAssessmentTitle ??
      null,
    assessmentRunId: run?.assessmentRunId ?? run?.id ?? null,
  }
}

function mergeRunAndRunView({ assessmentRun, assessmentRunView }, fallbackAssessmentId, fallbackAssessmentTitle) {
  const rawRun = assessmentRunView && assessmentRun
    ? { ...assessmentRunView, ...assessmentRun }
    : (assessmentRunView ?? assessmentRun)
  return normalizeRun(rawRun, fallbackAssessmentId, fallbackAssessmentTitle)
}

// Wrapper with explicit size so react-d3-speedometer SVG renders (fluidWidth needs parent dimensions)
const SPEEDOMETER_SIZE = 220

function SpeedometerBlock({ resultItem, indexResultItem }) {
  const rangeList = resultItem?.scoringRangeList ?? []
  const lengthOfScoringRange = rangeList.length
  if (lengthOfScoringRange === 0) return null
  const first = getMinMax(rangeList[0])
  const last = getMinMax(rangeList[lengthOfScoringRange - 1])
  const minValueOfScoringRange = first.min
  const maxValueOfScoringRange = last.max
  let userScoreValue = resultItem?.userScore
  if (userScoreValue > maxValueOfScoringRange) userScoreValue = maxValueOfScoringRange
  const scoringRangeItem = rangeList.find((r) => {
    const { min, max } = getMinMax(r)
    return min <= resultItem?.userScore && resultItem?.userScore <= max
  })
  const customSegmentStops = [
    minValueOfScoringRange,
    ...rangeList.map((r) => getMinMax(r).max),
  ]
  const colors = ASSESSMENT_RESULT_COLORS[indexResultItem % ASSESSMENT_RESULT_COLORS.length]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
      <Box
        sx={{
          width: SPEEDOMETER_SIZE,
          maxWidth: '100%',
          height: SPEEDOMETER_SIZE * 0.65,
          minHeight: 140,
          mx: 'auto',
          '& svg': { overflow: 'visible' },
        }}
      >
        <ReactSpeedometer
          customSegmentStops={customSegmentStops}
          fluidWidth
          value={userScoreValue}
          minValue={minValueOfScoringRange}
          maxValue={maxValueOfScoringRange}
          needleColor="#1a1a1a"
          startColor={colors?.startColor}
          endColor={colors?.endColor}
          segments={lengthOfScoringRange}
          needleHeightRatio={0.7}
          ringWidth={28}
          needleTransitionDuration={1200}
          needleTransition="easeExpOut"
          textColor="#374151"
        />
      </Box>
      {scoringRangeItem && (
        <Typography
          variant="h6"
          sx={{
            mt: 1.5,
            fontWeight: 600,
            color: colors?.endColor ?? 'text.primary',
            textAlign: 'center',
          }}
        >
          {scoringRangeItem.title}
        </Typography>
      )}
    </Box>
  )
}

function isDstProtocolRun(run) {
  const protocolType = run?.derivedMetrics?.protocolType ?? run?.run?.derivedMetrics?.protocolType ?? null
  return String(protocolType ?? '').toUpperCase() === 'DST'
}

function getScoringTrace(run) {
  return run?.scoringTrace ?? run?.run?.scoringTrace ?? {}
}

function getDstSummaryCards(derivedMetrics) {
  return [
    { key: 'chronologicalAgeDisplay', label: 'Chronological age', value: derivedMetrics?.chronologicalAgeDisplay ?? 'Not set' },
    { key: 'developmentalAgeDisplay', label: 'Developmental age', value: derivedMetrics?.developmentalAgeDisplay ?? 'Not set' },
    { key: 'developmentalQuotient', label: 'Developmental Quotient', value: derivedMetrics?.developmentalQuotient ?? 'Not scored' },
    { key: 'actualAgeBand', label: 'Actual age band', value: derivedMetrics?.actualAgeBand ?? 'Not set' },
    { key: 'recommendedStartBand', label: 'Recommended start band', value: derivedMetrics?.recommendedStartBand ?? 'Not set' },
    { key: 'highestPassedMilestoneAgeDisplay', label: 'Highest passed age', value: derivedMetrics?.highestPassedMilestoneAgeDisplay ?? 'Not set' },
    { key: 'maxConsecutiveFailCount', label: 'Max consecutive FAIL', value: String(derivedMetrics?.maxConsecutiveFailCount ?? 0) },
    { key: 'maxConsecutiveFailEndAgeDisplay', label: 'Ceiling reached at', value: derivedMetrics?.maxConsecutiveFailEndAgeDisplay ?? 'Not reached yet' },
  ]
}

function getStageSummaries(scoringTrace) {
  const stageBreakdown = scoringTrace?.stageBreakdown
  if (!stageBreakdown || typeof stageBreakdown !== 'object') return []
  return Object.values(stageBreakdown).filter(Boolean)
}

function getDstDobAuditRows(derivedMetrics) {
  return [
    { key: 'ageSourceLabel', label: 'Age source', value: derivedMetrics?.ageSourceLabel ?? null },
    { key: 'estimatedDateOfBirth', label: 'Estimated DOB', value: derivedMetrics?.estimatedDateOfBirth ?? null },
    { key: 'providedDateOfBirth', label: 'Provided DOB', value: derivedMetrics?.providedDateOfBirth ?? null },
    { key: 'dateOfBirthOverride', label: 'Practitioner override DOB', value: derivedMetrics?.dateOfBirthOverride ?? null },
    { key: 'dateOfBirthUsed', label: 'DOB used for scoring', value: derivedMetrics?.dateOfBirthUsed ?? null },
  ].filter((item) => item.value)
}

export default function PractitionerAssessmentResultView({
  triggeringBookedEventId,
  assessmentId,
  assessmentTitle,
  viewMode = 'view',
  /** Completed run id (ARUN-...) for GET /assessments/run/result. */
  completedRunId = null,
  initialRun = null,
  initialAssessmentReport = null,
  onBack,
  onClose,
  onViewAnswers,
  /** Pre-loaded AssessmentReport (e.g. from session auxiliaryData); if omitted, fetched by event id */
  assessmentReport: assessmentReportProp,
  /** Required for saving freeFormRichText to AssessmentReport */
  patientId,
  mentorId,
  onReportSaved,
  /** When true, use assessmentEventBookedEventId for runs API (assessment report page). */
  isAssessmentEventPage = false,
}) {
  const resolvedInitialReport =
    initialAssessmentReport ??
    (isAssessmentEventPage ? (assessmentReportProp ?? null) : null)
  const [loading, setLoading] = useState(() => !initialRun)
  const [run, setRun] = useState(initialRun ?? null)
  const [articleDrawer, setArticleDrawer] = useState(null) // { domainId, domainName, article, articleTitle }
  const [report, setReport] = useState(resolvedInitialReport)
  const [freeFormRichText, setFreeFormRichText] = useState(
    resolvedInitialReport?.freeFormRichText ?? resolvedInitialReport?.content ?? '',
  )
  const [narrativeSectionValues, setNarrativeSectionValues] = useState(
    () => buildNarrativeFieldState(resolvedInitialReport),
  )
  const [isEditingReport, setIsEditingReport] = useState(viewMode === 'edit')
  const [saveMenuAnchor, setSaveMenuAnchor] = useState(null)
  const [savingReportAction, setSavingReportAction] = useState(null) // 'draft' | 'completed' | null
  const [downloadingReportPdf, setDownloadingReportPdf] = useState(false)
  const syncReportState = useCallback((nextReport) => {
    setReport(nextReport ?? null)
    setFreeFormRichText(nextReport?.freeFormRichText ?? nextReport?.content ?? '')
    setNarrativeSectionValues(buildNarrativeFieldState(nextReport))
  }, [])
  const loadResultData = useCallback(async () => {
    if (!triggeringBookedEventId && !completedRunId) return
    const hasInitialRun = Boolean(initialRun)
    const hasInitialReport = Boolean(initialAssessmentReport)
    if (hasInitialRun && hasInitialReport) {
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      // Prefer new dedicated endpoint when a completed run id is known.
      if (completedRunId && (!hasInitialRun || !hasInitialReport)) {
        const runResult = await getAssessmentRunResult({ completedRunId })
        if (!runResult?.error) {
          const mergedRun = mergeRunAndRunView(
            {
              assessmentRun: runResult?.assessmentRun,
              assessmentRunView: runResult?.assessmentRunView,
            },
            assessmentId,
            assessmentTitle,
          )
          if (mergedRun) setRun(mergedRun)
          if (runResult?.assessmentReport) {
            syncReportState(runResult.assessmentReport)
          }
          if (mergedRun || runResult?.assessmentReport) return
        }
      }

      // Result view should stay completed-only; listing/drawer uses includeDrafts=true for status markers.
      const runsParams = isAssessmentEventPage
        ? { bookedEventId: triggeringBookedEventId, includeDrafts: false }
        : { parentBookedEventId: triggeringBookedEventId, includeDrafts: false }
      const { assessmentRuns, assessmentReport: reportFromRuns } = await getAssessmentRuns(runsParams)
      const found = (assessmentRuns ?? []).find((r) => getRunAssessmentId(r) === assessmentId)
      setRun(normalizeRun(found, assessmentId, assessmentTitle))
      const reportEventId = resolveAssessmentEventBookedEventId({
        run: found,
        report: reportFromRuns,
        triggeringBookedEventId,
        isAssessmentEventPage,
      })
      const canUseReportFromRuns = Boolean(
        reportFromRuns && (
          isAssessmentEventPage
            || !reportFromRuns?.bookedEventId
            || reportFromRuns?.bookedEventId === reportEventId
        ),
      )
      if (canUseReportFromRuns) {
        syncReportState(reportFromRuns)
      } else if (reportEventId) {
        const { assessmentReport: reportFromMentor } = await getAssessmentReportByEventIdMentor(reportEventId)
        if (reportFromMentor) {
          syncReportState(reportFromMentor)
        }
      }
    } catch {
      setRun(null)
    } finally {
      setLoading(false)
    }
  }, [
    triggeringBookedEventId,
    completedRunId,
    initialRun,
    initialAssessmentReport,
    assessmentId,
    assessmentTitle,
    isAssessmentEventPage,
    syncReportState,
  ])

  useEffect(() => {
    loadResultData()
  }, [loadResultData])

  useEffect(() => {
    if (isAssessmentEventPage && assessmentReportProp) {
      syncReportState(assessmentReportProp)
    }
  }, [assessmentReportProp, isAssessmentEventPage, syncReportState])

  useEffect(() => {
    setRun(initialRun ?? null)
    setLoading(!initialRun)
  }, [initialRun, assessmentId])

  useEffect(() => {
    if (!initialAssessmentReport) return
    // Apply once per assessment switch. Do not keep re-applying this snapshot on every parent prop change.
    syncReportState(initialAssessmentReport)
  }, [initialAssessmentReport, assessmentId, syncReportState])

  useEffect(() => {
    setIsEditingReport(viewMode === 'edit')
  }, [viewMode, assessmentId])

  useEffect(() => {
    if (!isEditingReport) setSaveMenuAnchor(null)
  }, [isEditingReport])

  const resolvedAssessmentEventBookedEventId = resolveAssessmentEventBookedEventId({
    run,
    report,
    triggeringBookedEventId,
    isAssessmentEventPage,
  })

  const handleSaveReport = useCallback(
    async (action) => {
      if (patientId == null || mentorId == null) {
        toast.error('Cannot save: session context (patient/mentor) is missing.')
        return
      }
      if (!resolvedAssessmentEventBookedEventId) {
        toast.error('Assessment booked event id is missing. Please refresh and try again.')
        return
      }
      const markCompleted = action === 'completed'
      const reportForSaveBase =
        report?.bookedEventId && report?.bookedEventId !== resolvedAssessmentEventBookedEventId
          ? {}
          : (report ?? {})
      const reportForSave = {
        ...reportForSaveBase,
        ...narrativeSectionValues,
      }
      setSavingReportAction(action)
      try {
        const { success, assessmentReport: saved, message } = await saveAssessmentReportFreeForm({
          bookedEventId: resolvedAssessmentEventBookedEventId,
          patientId,
          mentorId,
          freeFormRichText,
          status: markCompleted ? 'completed' : 'draft',
          report: reportForSave,
        })
        if (!success) {
          toast.error(message || 'Failed to save assessment report.')
          return
        }

        const mergedSavedReport = saved
          ? { ...reportForSave, ...saved }
          : reportForSave
        syncReportState(mergedSavedReport)
        if (markCompleted) {
          const startRes = await markBookedEventAsStarted(resolvedAssessmentEventBookedEventId)
          if (!startRes?.success) {
            if (typeof window !== 'undefined' && typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
              console.warn('[PractitionerAssessment] Event start failed before completion', {
                bookedEventId: resolvedAssessmentEventBookedEventId,
                startRes,
              })
            }
            toast.error('Assessment report saved, but failed to start this assessment session before completion.')
            onReportSaved?.()
            return
          }

          const completeRes = await markBookedEventAsCompleted(resolvedAssessmentEventBookedEventId)
          if (!completeRes?.success) {
            if (typeof window !== 'undefined' && typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
              console.warn('[PractitionerAssessment] Event finish failed after completed report save', {
                bookedEventId: resolvedAssessmentEventBookedEventId,
                completeRes,
              })
            }
            toast.error('Assessment report saved, but failed to mark assessment completed.')
            onReportSaved?.()
            return
          }
          toast.success('Assessment report saved and assessment marked completed.')
        } else {
          toast.success('Assessment report saved as draft.')
        }
        onReportSaved?.()
      } catch {
        toast.error(markCompleted ? 'Failed to save and complete assessment.' : 'Failed to save draft.')
      } finally {
        setSavingReportAction(null)
      }
    },
    [
      resolvedAssessmentEventBookedEventId,
      patientId,
      mentorId,
      freeFormRichText,
      report,
      narrativeSectionValues,
      onReportSaved,
      syncReportState,
    ],
  )

  const handleDownloadReportPdf = useCallback(async () => {
    if (!resolvedAssessmentEventBookedEventId) {
      toast.error('Assessment booked event id is missing. Please refresh and try again.')
      return
    }
    setDownloadingReportPdf(true)
    try {
      const result = await getAssessmentReportPdf(resolvedAssessmentEventBookedEventId)
      if (result?.error) {
        toast.error(result.error.message || 'Failed to download report')
        return
      }
      if (!result?.blob) {
        toast.error('Failed to download report')
        return
      }
      const downloadUrl = URL.createObjectURL(result.blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = result.filename || `${assessmentTitle || 'assessment-report'}.pdf`
      document.body.appendChild(link)
      link.click()
      URL.revokeObjectURL(downloadUrl)
      document.body.removeChild(link)
      toast.success('Report download started')
    } catch {
      toast.error('Failed to download report')
    } finally {
      setDownloadingReportPdf(false)
    }
  }, [resolvedAssessmentEventBookedEventId, assessmentTitle])

  const hasArticleContent = (payload) => {
    if (!payload) return false
    const content = payload?.article?.content ?? payload?.content
    return typeof content === 'string' && content.trim().length > 0
  }

  const openArticle = (domainId, domainName, articlePayload) => {
    if (!articlePayload || !hasArticleContent(articlePayload)) return
    setArticleDrawer({
      domainId,
      domainName,
      article: articlePayload.article ?? articlePayload,
      articleTitle: articlePayload.articleTitle ?? articlePayload?.title ?? 'Detailed report',
    })
  }

  const closeArticle = () => setArticleDrawer(null)

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!run) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">No results found for this assessment.</Typography>
        <Button
          type="button"
          startIcon={<ListIcon />}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onBack?.()
          }}
          sx={{ mt: 2 }}
        >
          Back to list
        </Button>
      </Box>
    )
  }

  const domainResults = run.domainResults ?? run.run?.domainResults ?? []
  const derivedMetrics = run?.derivedMetrics ?? run?.run?.derivedMetrics ?? {}
  const scoringTrace = getScoringTrace(run)
  const derivedMetricEntries = Object.entries(derivedMetrics).filter(([, value]) => {
    if (typeof value === 'number' || typeof value === 'boolean') return true
    if (typeof value === 'string') return value.trim().length > 0 && value.trim().length <= 80
    return false
  })
  const isCustomProtocolRun = String(run?.runnerType ?? run?.run?.runnerType ?? '').toUpperCase() === 'CUSTOM_PROTOCOL'
  const isDstRun = isCustomProtocolRun && isDstProtocolRun(run)
  const dstSummaryCards = isDstRun ? getDstSummaryCards(derivedMetrics) : []
  const dstDobAuditRows = isDstRun ? getDstDobAuditRows(derivedMetrics) : []
  const dstStageSummaries = isDstRun ? getStageSummaries(scoringTrace) : []
  const dstHighlightedMilestones = isDstRun
    ? [
        {
          key: 'developmentalAgeMilestone',
          label: 'Developmental age milestone',
          value: derivedMetrics?.developmentalAgeMilestone ?? null,
          secondary: null,
        },
        {
          key: 'highestPassedMilestone',
          label: 'Highest passed milestone',
          value: derivedMetrics?.highestPassedMilestone ?? null,
          secondary: derivedMetrics?.highestPassedMilestoneAgeDisplay ?? null,
        },
        {
          key: 'firstCeilingFailure',
          label: 'First ceiling failure',
          value: derivedMetrics?.firstCeilingFailure ?? null,
          secondary: derivedMetrics?.firstCeilingFailureAgeDisplay ?? null,
        },
      ].filter((item) => item.value)
    : []
  const runFlags = Array.isArray(run?.flags ?? run?.run?.flags) ? (run?.flags ?? run?.run?.flags) : []
  const runId = run?.id ?? run?.assessmentRunId
  const isSavingReport = Boolean(savingReportAction)
  const visibleNarrativeSections = ASSESSMENT_REPORT_EXTRA_NARRATIVE_SECTIONS.filter(
    (section) => isEditingReport || hasRichTextContent(narrativeSectionValues?.[section.field]),
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: (t) => alpha(t.palette.primary.main, 0.04),
        }}
      >
        <Typography variant="subtitle1" fontWeight={700} color="text.primary">
          {run.assessmentTitle ?? assessmentTitle ?? 'Results'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isEditingReport && (
            <Button
              type="button"
              variant="contained"
              size="small"
              startIcon={isSavingReport ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              endIcon={!isSavingReport ? <KeyboardArrowDownIcon /> : null}
              onClick={(e) => setSaveMenuAnchor(e.currentTarget)}
              disabled={isSavingReport || patientId == null || mentorId == null || !resolvedAssessmentEventBookedEventId}
              sx={{ mr: 0.5 }}
            >
              {isSavingReport
                ? (savingReportAction === 'completed' ? 'Saving & Completing…' : 'Saving Draft…')
                : 'Save'}
            </Button>
          )}
          {!isEditingReport && (
            <Button
              size="small"
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setIsEditingReport(true)
              }}
            >
              Edit Report
            </Button>
          )}
          {runId && onViewAnswers && !isCustomProtocolRun && (
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onViewAnswers(runId)
              }}
            >
              View Answers
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={downloadingReportPdf ? <CircularProgress size={14} /> : <DownloadIcon />}
            onClick={handleDownloadReportPdf}
            disabled={downloadingReportPdf || !resolvedAssessmentEventBookedEventId}
          >
            {downloadingReportPdf ? 'Downloading…' : 'Download'}
          </Button>
          <IconButton
            type="button"
            size="small"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onBack?.()
            }}
            aria-label="Show list of assessments"
          >
            <ListIcon />
          </IconButton>
          {onClose && (
            <IconButton
              type="button"
              size="small"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onClose()
              }}
              aria-label="Close"
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 3 }}>
        {isDstRun ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
                gap: 1.5,
              }}
            >
              {dstSummaryCards.map((card) => (
                <Paper
                  key={card.key}
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2.5,
                    border: 1,
                    borderColor: 'divider',
                    bgcolor: (theme) => alpha(theme.palette.background.paper, 0.94),
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {card.label}
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color="text.primary">
                    {String(card.value)}
                  </Typography>
                </Paper>
              ))}
            </Box>

            {derivedMetrics?.developmentalQuotientInterpretation && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  border: 1,
                  borderColor: 'divider',
                  bgcolor: (theme) => alpha(theme.palette.info.light, 0.12),
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.75 }}>
                  Interpretation
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {derivedMetrics.developmentalQuotientInterpretation}
                </Typography>
              </Paper>
            )}

            {derivedMetrics?.ageRangeGuidance && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  border: 1,
                  borderColor: 'divider',
                  bgcolor: (theme) => alpha(theme.palette.warning.light, 0.14),
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.75 }}>
                  Age range guidance
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {derivedMetrics.ageRangeGuidance}
                </Typography>
              </Paper>
            )}

            {dstDobAuditRows.length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  border: 1,
                  borderColor: 'divider',
                  bgcolor: (theme) => alpha(theme.palette.background.paper, 0.94),
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  Age reference used
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                    gap: 1,
                  }}
                >
                  {dstDobAuditRows.map((row) => (
                    <Box key={row.key}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {row.label}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {row.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            )}

            {dstHighlightedMilestones.length > 0 && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                  gap: 1.5,
                }}
              >
                {dstHighlightedMilestones.map((milestone) => (
                  <Paper
                    key={milestone.key}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2.5,
                      border: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {milestone.label}
                    </Typography>
                    <Typography variant="body1" fontWeight={700} color="text.primary">
                      {milestone.value}
                    </Typography>
                    {milestone.secondary && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {milestone.secondary}
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Box>
            )}

            {dstStageSummaries.length > 0 && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                  gap: 1.5,
                }}
              >
                {dstStageSummaries.map((stage, indexStage) => (
                  <Paper
                    key={stage?.domainId ?? `stage-${indexStage}`}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2.5,
                      border: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={700}>
                      {stage?.stageTitle ?? stage?.domainId ?? 'Stage'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                      Answered {stage?.answeredItems ?? 0} / {stage?.totalItems ?? 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Pass {stage?.passCount ?? 0} | Fail {stage?.failCount ?? 0} | Not testable {stage?.notTestableCount ?? 0}
                    </Typography>
                    {stage?.highestPassedMilestone && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                        Highest passed: {stage.highestPassedMilestone}
                        {stage?.highestPassedMilestoneAgeDisplay ? ` (${stage.highestPassedMilestoneAgeDisplay})` : ''}
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        ) : domainResults.length > 0 ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 2.25,
            }}
          >
            {domainResults.map((resultItem, indexResultItem) => {
              const isLastOddCard = domainResults.length % 2 === 1 && indexResultItem === domainResults.length - 1
              const rangeList = resultItem?.scoringRangeList ?? []
              const scoringRangeItem = rangeList.find((r) => {
                const { min, max } = getMinMax(r)
                return min <= resultItem?.userScore && resultItem?.userScore <= max
              })
              const articleForDomain = (run.articles ?? []).find((a) => a.domainId === resultItem?.domainId)
              const hasArticle = hasArticleContent(articleForDomain)
              const domainName = resultItem?.domainName ?? 'Domain'
              const stripColor = ASSESSMENT_RESULT_COLORS[indexResultItem % ASSESSMENT_RESULT_COLORS.length]

              return (
                <Paper
                  key={resultItem?.domainId ?? indexResultItem}
                  elevation={0}
                  sx={{
                    overflow: 'hidden',
                    gridColumn: { xs: 'auto', sm: isLastOddCard ? '1 / -1' : 'auto' },
                    borderRadius: 3,
                    border: 1,
                    borderColor: 'divider',
                    boxShadow: (t) => `0 4px 20px ${alpha(t.palette.common.black, 0.06)}`,
                    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                    '&:hover': {
                      boxShadow: (t) => `0 8px 28px ${alpha(t.palette.common.black, 0.08)}`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 1.25,
                      background: `linear-gradient(135deg, ${stripColor.startColor} 0%, ${stripColor.endColor} 100%)`,
                      color: '#1f2937',
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: '0.02em' }}>
                      {domainName}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 2.25, pt: 1 }}>
                    <SpeedometerBlock resultItem={resultItem} indexResultItem={indexResultItem} />
                    <Typography
                      variant="body1"
                      align="center"
                      sx={{
                        fontWeight: 600,
                        color: 'text.primary',
                        fontSize: '1rem',
                      }}
                    >
                      Score: {resultItem?.userScore} / {resultItem?.totalScore}
                    </Typography>
                    {scoringRangeItem?.interpretation && (
                      <Typography
                        variant="body2"
                        align="center"
                        sx={{
                          mt: 1.5,
                          color: 'text.secondary',
                          lineHeight: 1.5,
                          maxWidth: 480,
                          mx: 'auto',
                        }}
                      >
                        {scoringRangeItem.interpretation}
                      </Typography>
                    )}
                    {hasArticle && (
                      <Typography
                        variant="body2"
                        component="button"
                        onClick={() =>
                          openArticle(
                            resultItem?.domainId,
                            resultItem?.domainName,
                            articleForDomain,
                          )
                        }
                        sx={{
                          display: 'block',
                          mx: 'auto',
                          mt: 2,
                          cursor: 'pointer',
                          textDecoration: 'none',
                          color: 'primary.main',
                          background: 'none',
                          border: 'none',
                          font: 'inherit',
                          fontWeight: 600,
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        Tap to view detailed report
                      </Typography>
                    )}
                  </Box>
                </Paper>
              )
            })}
          </Box>
        ) : derivedMetricEntries.length > 0 ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
              gap: 1.5,
            }}
          >
            {derivedMetricEntries.map(([metricKey, metricValue]) => (
              <Paper
                key={metricKey}
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  border: 1,
                  borderColor: 'divider',
                  bgcolor: (theme) => alpha(theme.palette.background.paper, 0.92),
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {metricKey.replace(/([A-Z])/g, ' $1').replace(/^./, (char) => char.toUpperCase())}
                </Typography>
                <Typography variant="h6" fontWeight={700} color="text.primary">
                  {String(metricValue)}
                </Typography>
              </Paper>
            ))}
          </Box>
        ) : (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No domain results to display.
          </Typography>
        )}
        {runFlags.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {runFlags.map((flag) => (
              <Paper
                key={flag}
                elevation={0}
                sx={{
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 999,
                  border: 1,
                  borderColor: 'divider',
                  bgcolor: (theme) => alpha(theme.palette.warning.light, 0.12),
                }}
              >
                <Typography variant="caption" fontWeight={700}>
                  {flag.replaceAll('-', ' ')}
                </Typography>
              </Paper>
            ))}
          </Box>
        )}

        {visibleNarrativeSections.length > 0 && (
          <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {visibleNarrativeSections.map((section) => {
              const sectionValue = narrativeSectionValues?.[section.field] ?? ''
              return (
                <Paper
                  key={section.id}
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: 1,
                    borderColor: 'divider',
                    overflow: 'hidden',
                    boxShadow: (t) => `0 4px 20px ${alpha(t.palette.common.black, 0.06)}`,
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 1.25,
                      bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                      borderBottom: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                      {section.title}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 2 }}>
                    {isEditingReport ? (
                      <PatientHistoryTipTapEditor
                        value={sectionValue}
                        onChange={(html) => {
                          setNarrativeSectionValues((prev) => ({
                            ...prev,
                            [section.field]: html ?? '',
                          }))
                        }}
                        readOnly={false}
                        placeholder={`Write ${String(section.title || '').toLowerCase()}...`}
                        containerDivStyles={{ minHeight: 120 }}
                      />
                    ) : hasRichTextContent(sectionValue) ? (
                      <Box sx={{ '& img': { maxWidth: '100%' }, '& a': { color: 'primary.main' } }}>
                        {parse(sectionValue)}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No details added.
                      </Typography>
                    )}
                  </Box>
                </Paper>
              )
            })}
          </Box>
        )}

        {/* Practitioner notes: rich text saved to AssessmentReport.freeFormRichText */}
        <Paper
          elevation={0}
          sx={{
            mt: 3,
            mb: 2,
            borderRadius: 3,
            border: 1,
            borderColor: 'divider',
            overflow: 'hidden',
            boxShadow: (t) => `0 4px 20px ${alpha(t.palette.common.black, 0.06)}`,
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.25,
              bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                Practitioner notes
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isEditingReport
                  ? 'Save as draft or mark completed for this assessment event.'
                  : 'Read-only view. Use Edit Report to update notes.'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ p: 2 }}>
            <PatientHistoryTipTapEditor
              value={freeFormRichText}
              onChange={setFreeFormRichText}
              readOnly={!isEditingReport}
              placeholder={isEditingReport ? 'Add notes, observations, or conclusions for this assessment...' : undefined}
              containerDivStyles={{ minHeight: 160 }}
            />
          </Box>
        </Paper>
      </Box>

      {isEditingReport && (
        <Menu
          anchorEl={saveMenuAnchor}
          open={Boolean(saveMenuAnchor)}
          onClose={() => setSaveMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            onClick={() => {
              setSaveMenuAnchor(null)
              handleSaveReport('draft')
            }}
            disabled={isSavingReport}
          >
            Save Draft (Complete Later)
          </MenuItem>
          <MenuItem
            onClick={() => {
              setSaveMenuAnchor(null)
              handleSaveReport('completed')
            }}
            disabled={isSavingReport}
          >
            Save and Mark Completed
          </MenuItem>
        </Menu>
      )}

      <Drawer
        anchor="right"
        open={Boolean(articleDrawer)}
        onClose={closeArticle}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 400 }, maxWidth: '100%' },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {articleDrawer?.articleTitle ?? articleDrawer?.domainName ?? 'Detailed report'}
            </Typography>
            <IconButton size="small" onClick={closeArticle} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Box>
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 2,
              '& img': { maxWidth: '100%' },
              '& a': { color: 'primary.main' },
            }}
          >
            {(() => {
              const html = articleDrawer?.article?.content ?? articleDrawer?.content
              if (typeof html === 'string' && html.trim()) return parse(html)
              return (
                <Typography color="text.secondary">Detailed report content is not available.</Typography>
              )
            })()}
          </Box>
        </Box>
      </Drawer>
    </Box>
  )
}
